import React, { useCallback, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
    Users, UserPlus, Trash2, Edit2, X, CheckCircle, AlertCircle, Eye, EyeOff
} from 'lucide-react';
import '../styles/AdminDashboard.css';
import { adminSchema, validatePasswordStrength, KNOWN_PLANTS } from '../schemas/adminSchema';

export default function AdminDashboard() {
    // State added for Modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(null);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const API_BASE_URL = process.env.REACT_APP_BACKEND_URI;

    // Load users on component mount
    const fetchUsers = useCallback(async () => {
        try {
            setIsLoadingUsers(true);
            const response = await fetch(`${API_BASE_URL}/api/admin/users`);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || data?.error || 'Failed to load users');
            }
            
            if (data.success && Array.isArray(data.users)) {
                setUsers(data.users);
                setSubmitStatus(null);
            } else {
                throw new Error('Unexpected response while loading users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setSubmitStatus({
                type: 'error',
                message: error.message || 'Error loading users. Please check server logs.'
            });
        } finally {
            setIsLoadingUsers(false);
        }
    }, [API_BASE_URL]);

    React.useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                setUsers(users.filter(user => user.id !== userId));
                alert('User deleted successfully');
            } else {
                alert('Failed to delete user: ' + (data.error || data.message));
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Error deleting user. Please try again.');
        }
    };

    const initialValues = {
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        confirm_password: '',
        role: 'AGM',
        plant: 'Ambatale WTP',
    };

    const handlePasswordChange = (password) => {
        const strength = validatePasswordStrength(password);
        setPasswordStrength(strength);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            // Call backend API to create user in Clerk
            const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    first_name: values.first_name,
                    last_name: values.last_name,
                    email: values.email,
                    username: values.username,
                    password: values.password,
                    role: values.role,
                    plant: values.plant
                })
            });

            const data = await response.json();

            if (!response.ok) {
                const detailMessage = Array.isArray(data?.details) && data.details.length > 0
                    ? (data.details[0]?.longMessage || data.details[0]?.message)
                    : null;
                throw new Error(detailMessage || data.error || data.message || 'Failed to create user');
            }

            setSubmitStatus({ 
                type: 'success', 
                message: 'User added successfully!' 
            });
            setSubmitting(false);
            resetForm();
            setPasswordStrength(null);
            
            // Refresh users list
            fetchUsers();
            
            // Close modal and clear status after 3 seconds
            setTimeout(() => {
                setIsModalOpen(false);
                setSubmitStatus(null);
            }, 3000);
        } catch (error) {
            console.error('Error creating user:', error);
            setSubmitStatus({ 
                type: 'error', 
                message: error.message || 'Error adding user. Please try again.' 
            });
            setSubmitting(false);
        }
    };

    const ErrorDisplay = ({ name }) => (
        <ErrorMessage name={name}>
            {msg => (
                <div className="error-message">
                    <AlertCircle className="w-4 h-4" />
                    {msg}
                </div>
            )}
        </ErrorMessage>
    );

    return (
        <div className="dashboard-container">
            <div className="dashboard-content">

                {/* --- Header Section --- */}
                <div className="page-header">
                    <h1 className="page-title">Admin Dashboard</h1>
                    {/* Added onClick handler */}
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="btn-add"
                    >
                        <UserPlus className="w-4 h-4" />
                        Add New User
                    </button>
                </div>

                {/* --- Summary Cards --- */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon-wrapper blue">
                            <Users className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="stat-label">Total Users</p>
                            <p className="stat-value">{users.length}</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon-wrapper green">
                            <CheckCircle className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="stat-label">Active Now</p>
                            <p className="stat-value">{users.length}</p>
                        </div>
                    </div>
                </div>

                {/* --- User Table --- */}
                <div className="table-container">
                    {isLoadingUsers ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            <p>Loading users...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            <p>No users found. Create your first user by clicking "Add New User".</p>
                        </div>
                    ) : (
                        <table className="user-table">
                            <thead>
                            <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Plant</th>
                                <th className="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map((user) => (
                                <tr key={user.id}>
                                    <td className="table-text-primary">{user.firstName} {user.lastName}</td>
                                    <td className="table-text-secondary">{user.email}</td>
                                    <td>
                        <span className={`badge ${user.role === 'AGM' ? 'badge-cyan' : user.role === 'Administrator' ? 'badge-purple' : 'badge-yellow'}`}>
                          {user.role}
                        </span>
                                    </td>
                                    <td className="table-text-secondary">{user.plant}</td>
                                    <td className="action-cell">
                                        <button className="btn-icon" title="Edit"><Edit2 className="w-4 h-4" /></button>
                                        <button 
                                            className="btn-icon delete" 
                                            title="Delete"
                                            onClick={() => handleDeleteUser(user.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* --- "Add New User" Modal (Newly Added) --- */}
                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-drawer">

                            <div className="modal-header">
                                <h2 className="modal-title">Add new user</h2>
                                <button onClick={() => setIsModalOpen(false)} className="btn-close">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Status Messages */}
                            {submitStatus && (
                                <div className={`status-banner ${submitStatus.type}`}>
                                    <AlertCircle className="w-5 h-5" />
                                    {submitStatus.message}
                                </div>
                            )}

                            <Formik
                                initialValues={initialValues}
                                validationSchema={adminSchema}
                                onSubmit={handleSubmit}
                                validateOnChange={true}
                                validateOnBlur={true}
                            >
                            {({ isSubmitting, submitCount, errors }) => (
                            <Form className="form-stack">
                                {submitCount > 0 && Object.keys(errors).length > 0 && (
                                    <div className="status-banner error">
                                        <AlertCircle className="w-5 h-5" />
                                        Please fix the highlighted fields before submitting.
                                    </div>
                                )}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>First Name <span className="required-mark">*</span></label>
                                        <Field
                                            type="text"
                                            name="first_name"
                                            className="form-input"
                                            placeholder="e.g., John"
                                        />
                                        <ErrorDisplay name="first_name" />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Name <span className="required-mark">*</span></label>
                                        <Field
                                            type="text"
                                            name="last_name"
                                            className="form-input"
                                            placeholder="e.g., Doe"
                                        />
                                        <ErrorDisplay name="last_name" />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Email <span className="required-mark">*</span></label>
                                    <Field
                                        type="email"
                                        name="email"
                                        className="form-input"
                                        placeholder="e.g., john.doe@example.com"
                                    />
                                    <ErrorDisplay name="email" />
                                    <small className="field-hint">Must be a valid email address</small>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Username <span className="required-mark">*</span></label>
                                        <Field
                                            type="text"
                                            name="username"
                                            className="form-input"
                                            placeholder="e.g., john_doe"
                                        />
                                        <ErrorDisplay name="username" />
                                        <small className="field-hint">3-30 characters, alphanumeric, dots, underscores, and hyphens</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Temporary Password <span className="required-mark">*</span></label>
                                        <div className="password-wrapper">
                                            <Field name="password">
                                                {({ field, form }) => (
                                                    <input
                                                        {...field}
                                                        type={showPassword ? 'text' : 'password'}
                                                        className="form-input"
                                                        placeholder="Min 8 chars, mix of A-Z, a-z, 0-9, @$!%*?&"
                                                        onChange={(e) => {
                                                            const nextValue = e.target.value;
                                                            form.setFieldValue('password', nextValue);
                                                            handlePasswordChange(nextValue);
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="password-toggle"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <ErrorDisplay name="password" />
                                        {passwordStrength && (
                                            <div className="password-strength">
                                                <div className="strength-bar">
                                                    <div 
                                                        className={`strength-fill strength-${passwordStrength.strength}`}
                                                    ></div>
                                                </div>
                                                <div className="strength-feedback">
                                                    {passwordStrength.feedback.map((item, idx) => (
                                                        <small key={idx} className="feedback-item">• {item}</small>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Confirm Password <span className="required-mark">*</span></label>
                                        <Field
                                            type={showPassword ? 'text' : 'password'}
                                            name="confirm_password"
                                            className="form-input"
                                            placeholder="Re-enter password"
                                        />
                                        <ErrorDisplay name="confirm_password" />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Role <span className="required-mark">*</span></label>
                                        <Field
                                            as="select"
                                            name="role"
                                            className="form-select"
                                        >
                                            <option value="AGM">AGM</option>
                                            <option value="Shift Manager">Shift Manager</option>
                                            <option value="Administrator">Administrator</option>
                                        </Field>
                                        <ErrorDisplay name="role" />
                                    </div>

                                    <div className="form-group">
                                        <label>Plant <span className="required-mark">*</span></label>
                                        <Field
                                            as="select"
                                            name="plant"
                                            className="form-select"
                                        >
                                            {KNOWN_PLANTS.map((plant) => (
                                                <option key={plant} value={plant}>{plant}</option>
                                            ))}
                                        </Field>
                                        <ErrorDisplay name="plant" />
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="btn-cancel"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add'}
                                    </button>
                                </div>

                            </Form>
                            )}
                            </Formik>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
